/**
 * "Second look" for quotes (Corbin, 10/28; migration 702).
 *
 * Regina and Darcy look a quote over before it goes out. Looking never sends
 * it back to Corbin. Only a real edit (a line, a price, a choice) does, and
 * the screen says so BEFORE that edit is saved.
 *
 *   SecondLookBar          — "Second look ✓" button + who looked, when.
 *   ApprovedEditWarning    — amber strip on an approved quote: editing sends it back.
 *   EditApprovedConfirm    — the "Edit an approved quote?" question before edit mode.
 *   QuoteApprovalStrip     — the quote page's version (self-fetching).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, Loader2, PencilLine } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { InfoButton, type InfoTopic } from "@/components/info-button";

export type ApprovalState = "none" | "pending" | "approved" | "delta";

export interface SecondLook {
    byName: string | null;
    at: string;
    approvalState: string | null;
}

export interface ApprovalLook {
    status: string;
    approvalState: ApprovalState;
    approvedByName: string | null;
    approvedAt: string | null;
    looks: SecondLook[];
}

export const SECOND_LOOK_HELP: InfoTopic = {
    title: "What Second look does",
    lines: [
        "It marks that you looked this quote over.",
        "It changes nothing. The lines, the prices and the approval stay exactly as they are.",
        "Everyone sees who looked and when.",
        "Changing a line, a price or a choice is different: that sends the quote back for approval again.",
    ],
};

export const approvalLookKey = (estimateId: string) => ["quote-approval-look", estimateId] as const;

/** "Regina · Oct 28, 2:14 PM" */
export function lookLine(look: SecondLook): string {
    const when = new Date(look.at).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
    const who = look.byName?.split(" ")[0] || "Someone";
    return `${who} · ${when}`;
}

/** The approver's first name, for "sends it back to Corbin". */
function approverWord(name: string | null | undefined): string {
    return name?.trim().split(/\s+/)[0] || "the owner";
}

export function SecondLookBar({ estimateId, className }: { estimateId: string; className?: string }) {
    const { isAdmin } = useAuth();
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: approvalLookKey(estimateId),
        queryFn: () => api.get<ApprovalLook>(`/api/estimates/${estimateId}/approval-look`),
        staleTime: 15_000,
    });
    const look = useMutation({
        mutationFn: () => api.post<{ looks: SecondLook[] }>(`/api/close-out/estimates/${estimateId}/second-look`, {}),
        onSuccess: (r) => {
            toast.success("Second look saved. Nothing on the quote changed.");
            qc.setQueryData<ApprovalLook | undefined>(approvalLookKey(estimateId), (old) =>
                old ? { ...old, looks: r.looks } : old,
            );
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't save that. Try again."),
    });
    const latest = q.data?.looks?.[0] ?? null;
    // Corbin approves; the office gets the button. Everyone sees the record.
    const showButton = !isAdmin;
    if (!showButton && !latest) return null;
    return (
        <div className={cn("flex flex-wrap items-center gap-2", className)} data-testid="second-look-bar">
            {showButton && (
                <Button
                    size="sm"
                    variant="outline"
                    className="border-sky-600/60 text-sky-800 hover:bg-sky-50 dark:text-sky-200 dark:hover:bg-sky-950/30"
                    disabled={look.isPending}
                    onClick={() => look.mutate()}
                    data-testid="second-look-button"
                >
                    {look.isPending ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <Eye className="mr-1 h-3.5 w-3.5" aria-hidden />
                    )}
                    Second look ✓
                </Button>
            )}
            {latest && (
                <span
                    className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[11.5px] font-bold text-sky-900 dark:bg-sky-900/40 dark:text-sky-100"
                    title={(q.data?.looks ?? []).map(lookLine).join("\n")}
                    data-testid="second-look-latest"
                >
                    ✓ Second look: {lookLine(latest)}
                </span>
            )}
            {showButton && <InfoButton topic={SECOND_LOOK_HELP} />}
        </div>
    );
}

/** Amber strip on an approved quote: editing sends it back. Plain, before any save. */
export function ApprovedEditWarning({ approverName, className }: { approverName?: string | null; className?: string }) {
    const who = approverWord(approverName);
    return (
        <div
            className={cn(
                "flex items-start gap-2 rounded-md border-l-[3px] border-amber-500 bg-amber-50 px-2.5 py-2 text-[12.5px] text-amber-950 dark:bg-amber-950/30 dark:text-amber-100",
                className,
            )}
            data-testid="approved-edit-warning"
        >
            <PencilLine className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
            <span>
                <b>{who === "the owner" ? "This quote is approved." : `${who} approved this quote.`}</b> Changing a
                line, a price or a choice sends it back to {who} to OK again. Just checking it? Tap <b>Second look ✓</b>{" "}
                instead.
            </span>
        </div>
    );
}

/** Asked before edit mode opens on an approved quote. */
export function EditApprovedConfirm({
    open,
    approverName,
    onCancel,
    onEdit,
}: {
    open: boolean;
    approverName?: string | null;
    onCancel: () => void;
    onEdit: () => void;
}) {
    const who = approverWord(approverName);
    return (
        <Dialog open={open} onClose={onCancel} title="Edit an approved quote?">
            <div className="space-y-3 text-[14px]" data-testid="edit-approved-confirm">
                <p>
                    {who === "the owner" ? "This quote is approved." : `${who} approved this quote.`} If you change a{" "}
                    <b>line</b>, a <b>price</b> or a <b>choice</b>, it goes back to {who} to OK again before it can be
                    sent.
                </p>
                <p className="text-foreground/70">Just looking it over? Use Second look ✓. It changes nothing.</p>
                <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="outline" onClick={onCancel} data-testid="edit-approved-cancel">
                        Just looking
                    </Button>
                    <Button
                        className="bg-amber-600 text-white hover:bg-amber-700"
                        onClick={onEdit}
                        data-testid="edit-approved-go"
                    >
                        Edit — it goes back to {who}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

/**
 * The quote page's strip: approval in one line, the editing warning when it is
 * approved, and Second look. Hidden for a quote outside the owner's gate.
 */
export function QuoteApprovalStrip({ estimateId }: { estimateId: string }) {
    const q = useQuery({
        queryKey: approvalLookKey(estimateId),
        queryFn: () => api.get<ApprovalLook>(`/api/estimates/${estimateId}/approval-look`),
        staleTime: 15_000,
    });
    const d = q.data;
    if (!d || !["draft", "sent"].includes(d.status)) return null;
    if (d.approvalState === "none" && d.looks.length === 0 && d.status !== "draft") return null;
    return (
        <div
            className="mt-3 space-y-2 rounded-lg border border-border bg-card p-2.5 text-foreground"
            data-testid="quote-approval-strip"
        >
            {d.approvalState === "approved" && <ApprovedEditWarning approverName={d.approvedByName} />}
            {d.approvalState === "delta" && (
                <div className="rounded-md border-l-[3px] border-amber-600 bg-amber-50 px-2.5 py-2 text-[12.5px] text-amber-950 dark:bg-amber-950/30 dark:text-amber-100">
                    <b>Changed since {approverWord(d.approvedByName)} approved it.</b> It needs an OK again before it
                    can be sent.
                </div>
            )}
            <SecondLookBar estimateId={estimateId} />
        </div>
    );
}
